import { DocumentType } from "../../../enums";

export interface MemberList {
  members: Member[];
}

export interface Phone {
  phone: string;
  phoneType: string;
}

export interface Member {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  imageUrl: string;
  numberOfDirectReports: string;
  roleName: string;
  teamName: string;
  tableData: any;
  teamNickname: string;
  latestCheckinCompleted: string | Date;
  completedCheckinsCount: string;
  yearsEmployed: number;
  checkinCompletionPercentage: string;
  missingCheckins: number;
  email: string;
  PTOUsagePercentage: string;
  PTOinHours: number;
  individualType: number;
  hireDate: string | Date;
  teamsLed: Array<string>;
  totalHoursWorked: number;
  hoursPerWeek: number;
  overtimePerWeek: number;
  totalOvertime: number;
  associateId: string;
  score: number;
  cultureIndex: string;
}

export interface Individual {
  memberId: string;
  type?: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | undefined;
  phones?: Array<Phone>;
  birthDate: Date | undefined;
  hireDate: Date | undefined;
  deceasedDate: Date | undefined;
  gender: string | undefined;
  species: string | undefined;
  living: string | undefined;
  avatarImage: string | File | undefined;
}

export interface Favorite {
  id: string;
  questionId: string;
  answers: Array<string>;
}

export interface Vehicle {
  id: string;
  vehicleYear?: number;
  vehicleMake?: string;
  vehicleModel?: string;
  licensePlate?: string;
}

export interface Document {
  documentType?: string;
  documentFileName?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Attribute {
  attributeId: string;
  position: number;
}

export interface EmploymentInfo {
  employeeTitleId?: string;
  employeeTitle?: string;
  driversLicenseExpiration?: Date | string;
  workVisaRequired?: boolean;
  workVisaExpirationDate?: Date | string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  vehicles?: Array<Vehicle>;
  documents?: Array<Document>;
  strengthFinderAttributes?: Array<Attribute>;
}

export interface EmployeeInfo {
  employeeTitleId: string;
  vehicleId: string;
  vehicleYear: number;
  vehicleMake: string;
  vehicleModel: string;
  licensePlate: string;
  workVisaRequired: boolean;
  workVisaExpirationDate?: Date | string;
  driversLicenseExpiration?: Date | string;
  [DocumentType.CERTIFICATIONS]?: File;
  [DocumentType.DRIVERS_LICENSE]?: File;
  [DocumentType.EMPLOYMENT_CONTRACT]?: File;
  [DocumentType.POLICIES_ACKNOWLEDGEMENTS]?: File;
  [DocumentType.STATE_LICENSE]?: File;
}

export interface Checkin {
  id: string;
  timePeriod: string | Date;
  review: string;
  checkinUploadName: string;
  checkinType: number;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface CultureIndexEntry {
  associateId: string;
  chartName: string;
  classification: string;
  date: string | Date;
  euVal: number;
  pointColor: string;
  pointLetter: string;
  pointNum: number;
  pointRgb1: number;
  pointRgb2: number;
  pointRgb3: number;
  redArrowValue: number;
}

export interface RevenueEntry {
  amount: number;
  date: string | Date;
}

export interface MemberDetails {
  member: Individual;
  spouse?: Individual;
  children?: Array<Individual>;
  pets?: Array<Individual>;
  favorites?: Array<Favorite>;
  employmentInfo?: EmploymentInfo;
  associateId: string;
  checkins?: Array<Checkin>;
  cultureIndexEntries?: Array<CultureIndexEntry>;
  revenueEntries?: Array<RevenueEntry>;
  companyName?: string;
  companyId?: string;
  teamLead?: {
    memberId: string;
    name: string;
  };
}

export interface StrengthFinderDetails {
  id: string;
  themeColor: string;
  themeGroup: string;
  themeName: string;
  themeDesc: string;
}

export interface EmployeeTitle {
  id: string;
  name: string;
  createdAt: Date;
  updatedAt: Date;
}

export const MonthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export const MonthNamesShort = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

export interface MonthCheckIns {
  MonthlyCheckIn?: string;
  AnnualCheckIn?: string;
  NinetyDayCheckIn?: string;
}

export const today = new Date();
export const Years: Array<string> = [];
for (let i = 0; i < 2; i++) {
  Years.push((today.getFullYear() - i).toString());
}

export type Year = Record<typeof MonthNames[number], MonthCheckIns>;
export type CheckIns = Record<typeof Years[number], Partial<Year>>;

export const CheckInTypes = {
  annual: "Annual",
  monthly: "Monthly",
  ninety: "90 Day",
} as const;

export type CheckInName = typeof CheckInTypes[keyof typeof CheckInTypes];

export const CheckInRoutes: Record<CheckInName, string> = {
  Annual: "AnnualCheckIn",
  Monthly: "MonthlyCheckIn",
  "90 Day": "NinetyDayCheckIn",
} as const;

export interface CheckInProps {
  checkInType: CheckInName;
  year: string;
  month: string;
  checkInId: string;
}
export enum CheckinType {
  ANNUAL_CHECKIN = 0,
  MONTHLY_CHECKIN,
  NINETY_DAY_CHECKIN,
}
